// ═══════════════════════════════════════════════════════════════
// BLACKBOARD — Module Layout with sub-navigation
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Group, Button, Badge } from '@mantine/core';
import {
  IconLayoutDashboard, IconSchool, IconBook,
  IconCertificate, IconChartBar, IconClipboardList,
  IconBuildingCommunity, IconBell, IconReportAnalytics, IconMail,
} from '@tabler/icons-react';
import { useFilteredNav } from '../../hooks/useFilteredNav';
import { useBlackboardStore } from '../../store/blackboardStore';
import { useAuthStore } from '../../store/authStore';
import type { NavItem } from '../../types/nav';
import { brand } from '../../lib/colors';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/blackboard', icon: IconLayoutDashboard, exact: true },
  { label: 'My Training', path: '/blackboard/my-training', icon: IconSchool },
  { label: 'Classrooms', path: '/blackboard/classrooms', icon: IconBuildingCommunity },
  { label: 'Training Library', path: '/blackboard/library', icon: IconBook },
  { label: 'Records', path: '/blackboard/records', icon: IconClipboardList, minLevel: 'editor' },
  { label: 'Position Plans', path: '/blackboard/positions', icon: IconCertificate, minLevel: 'editor' },
  { label: 'Certificates', path: '/blackboard/certificates', icon: IconCertificate, minLevel: 'editor' },
  { label: 'Analytics', path: '/blackboard/analytics', icon: IconChartBar, minLevel: 'editor' },
  { label: 'Reports', path: '/blackboard/reports', icon: IconReportAnalytics, minLevel: 'editor' },
  { label: 'Communications', path: '/blackboard/comms', icon: IconMail, minLevel: 'editor' },
  { label: 'Notifications', path: '/blackboard/notifications', icon: IconBell },
];

export default function BlackboardLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const visibleItems = useFilteredNav('blackboard', NAV_ITEMS);
  const { messages, loadMessages } = useBlackboardStore();
  const { user } = useAuthStore();

  useEffect(() => { loadMessages(); }, [loadMessages]);

  const unreadCount = useMemo(() => {
    const email = user?.email?.toLowerCase() || '';
    if (!email) return 0;
    return messages.filter(m =>
      m.recipientEmail?.toLowerCase() === email &&
      (m.channel === 'inbox' || m.channel === 'both') &&
      !m.readAt
    ).length;
  }, [messages, user?.email]);

  const currentPage = NAV_ITEMS.find(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );

  return (
    <div style={{ padding: '24px' }}>
      <Breadcrumbs
        items={[
          { label: 'Hub', to: '/' },
          { label: 'Blackboard', to: '/blackboard' },
          ...(currentPage && currentPage.path !== '/blackboard' ? [{ label: currentPage.label }] : []),
        ]}
      />
      <Group gap="xs" mb="md" style={{ borderBottom: '1px solid var(--frost)', paddingBottom: 12, flexWrap: 'wrap' }}>
        {visibleItems.map(item => {
          const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
          const isMyTraining = item.path === '/blackboard/my-training';
          return (
            <Button
              key={item.path}
              variant={active ? 'filled' : 'light'}
              color={active ? brand.blurple : 'gray'}
              size="xs"
              leftSection={<item.icon size={14} />}
              onClick={() => navigate(item.path)}
              rightSection={isMyTraining && unreadCount > 0 ? (
                <Badge size="xs" circle color="red" variant="filled">{unreadCount}</Badge>
              ) : undefined}
            >
              {item.label}
            </Button>
          );
        })}
      </Group>
      <Outlet />
    </div>
  );
}
